import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';


const ActualiteDetail = () => {
    const { id } = useParams();
    const [actualite, setActualite] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchActualite = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/actualite/${id}?populate=*`, {
                    headers: {
                        Authorization: `${process.env.REACT_APP_API_TOKEN}`
                    }
                });
                setActualite(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching actualite:', error);
                setLoading(false);
            }
        };

        fetchActualite();
    }, [id]);

    if (loading) return <div>Loading...</div>;
    if (!actualite) return <div>Article non trouvé</div>;


    return (
        <div className="min-h-screen bg-cream">
            {/* Hero Section */}
            <section className="relative h-[70vh]">
                <img
                    src={`${actualite.imageCouverture.url}`}
                    alt={actualite.titre}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white px-4">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-4xl md:text-6xl font-cormorant mb-4"
                        >
                            {actualite.titre}
                        </motion.h1>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="flex items-center justify-center space-x-4 text-sm"
                        >
                            <span>{actualite.auteur}</span>
                            <span>•</span>
                            <span>{format(new Date(actualite.datePublication), 'dd MMMM yyyy', { locale: fr })}</span>
                            <span>•</span>
                            <span className="bg-brandy-rose text-white px-3 py-1 rounded-full">{actualite.category}</span>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Article Content */}
            <section className="max-w-4xl mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="prose prose-lg mx-auto"
                >
                    <div className="text-xl text-gray-600 mb-8 font-light">
                        {actualite.extrait}
                    </div>
                    
                    <div className="mt-8 prose prose-lg max-w-none">
                        <ReactMarkdown 
                            className="text-gray-800"
                            components={{
                                // Personnalisation des éléments markdown
                                h1: ({node, ...props}) => <h1 className="text-3xl font-bold mb-4" {...props} />,
                                h2: ({node, ...props}) => <h2 className="text-2xl font-bold mb-3" {...props} />,
                                h3: ({node, ...props}) => <h3 className="text-xl font-bold mb-2" {...props} />,
                                p: ({node, ...props}) => <p className="mb-4" {...props} />,
                                ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-4" {...props} />,
                                ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-4" {...props} />,
                                li: ({node, ...props}) => <li className="mb-2" {...props} />,
                                a: ({node, ...props}) => (
                                    <a className="text-cocoa hover:text-brandy-rose transition-colors" {...props} />
                                ),
                                blockquote: ({node, ...props}) => (
                                    <blockquote className="border-l-4 border-cocoa pl-4 italic my-4" {...props} />
                                ),
                                img: ({node, ...props}) => (
                                    <img className="rounded-lg shadow-lg my-4" {...props} />
                                ),
                            }}
                        >
                            {actualite.contenu}
                        </ReactMarkdown>
                    </div>
                </motion.div>

                {/* Gallery avec Masonry Layout */}
                {actualite?.galerie && actualite.galerie.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="mt-16"
                    >
                        <h2 className="text-2xl font-cormorant text-cocoa mb-8">Galerie</h2>
                        
                        {/* Grille Masonry responsive */}
                        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                            {actualite.galerie.map((image, index) => (
                                <motion.div
                                    key={index}
                                    className="break-inside-avoid"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <img
                                        src={`${image.url}`}
                                        alt={`Image ${index + 1}`}
                                        className="w-full rounded-lg shadow-lg"
                                        style={{ marginBottom: "1rem" }}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Back Button */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="mt-16 text-center"
                >
                    <Link
                        to="/actualites"
                        className="inline-flex items-center justify-center px-6 py-3 bg-cocoa text-cream rounded-full hover:bg-brandy-rose transition-colors"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Retour aux actualités
                    </Link>
                </motion.div>
            </section>
        </div>
    );
};

export default ActualiteDetail;